import {
    Configuration,
    Favorite,
    FavoriteApi,
    InlineResponse2005
} from 'hotdesk_typescript';

type FavoriteListParams = {
    userId?: number;
    listingId?: number;
    limit?: number;
    offset?: number;
};

/**
 * Build the positional args for `FavoriteApi.favoriteList`.
 * The generated client only accepts positional parameters, so this
 * helper keeps call-sites readable.
 */
function buildFavoriteListArgs(
    params: FavoriteListParams
): Parameters<FavoriteApi['favoriteList']> {
    // Positional order:
    //  0-5:  createdAt filters
    //  6-11: updatedAt filters
    //  12: userId, 13: userIdIn, 14-17: user filters, 18-19: listing title
    //  20: listingId, 21: listingIdIn, 22-23: listing status
    //  24: limit, 25: offset
    const args: (string | number | undefined)[] = new Array(26).fill(undefined);
    args[12] = params.userId;
    args[20] = params.listingId;
    args[24] = params.limit;
    args[25] = params.offset;
    return args as Parameters<FavoriteApi['favoriteList']>;
}

export class FavoriteService {
    private favoriteApi: FavoriteApi;

    constructor(apiConfig: Configuration) {
        this.favoriteApi = new FavoriteApi(apiConfig);
    }

    async addFavorite(userId: number, listingId: number): Promise<Favorite> {
        try {
            const data: Favorite = {
                user: userId,
                listing: listingId
            };
            return await this.favoriteApi.favoriteCreate(data);
        } catch (error) {
            console.error('Error adding favorite:', error);
            throw error;
        }
    }

    async removeFavorite(favoriteId: number): Promise<void> {
        try {
            await this.favoriteApi.favoriteDelete(favoriteId);
        } catch (error) {
            console.error('Error removing favorite:', error);
            throw error;
        }
    }

    async getUserFavorites(
        userId: number,
        limit: number = 100,
        offset: number = 0
    ): Promise<InlineResponse2005> {
        try {
            return await this.favoriteApi.favoriteList(
                ...buildFavoriteListArgs({ userId, limit, offset })
            );
        } catch (error) {
            console.error('Error fetching favorites:', error);
            throw error;
        }
    }

    async isListingFavorited(
        userId: number,
        listingId: number
    ): Promise<{ isFavorited: boolean; favoriteId?: number }> {
        try {
            const response = await this.favoriteApi.favoriteList(
                ...buildFavoriteListArgs({ userId, listingId, limit: 1 })
            );
            const results = (response as any).results || [];
            if (results.length > 0) {
                return { isFavorited: true, favoriteId: results[0].id };
            }
            return { isFavorited: false };
        } catch (error) {
            console.error('Error checking favorite status:', error);
            return { isFavorited: false };
        }
    }
}
